import { Component, ErrorBoundary } from "solid-js";
import { Router, Route, Navigate } from "@solidjs/router";
import { ApiError } from "./api/client";
import { AppShell, RouteErrorBoundary } from "./components/layout";
import { ToastProvider } from "./contexts/ToastContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { TooltipProvider } from "./components/Tooltip";
import {
  BacklogPage,
  BillingPage,
  BoardPage,
  ChatPage,
  DecisionsPage,
  DocumentDetailPage,
  DocumentsPage,
  GitHubAppCallbackPage,
  InvitePage,
  ItemDetailPage,
  LandingPage,
  LegalPage,
  LoginPage,
  PendingPage,
  PlanPage,
  ProjectsPage,
  ProjectSettingsPage,
} from "./pages";

/**
 * Renders a page inside the shell, with its own error boundary between the
 * two. A render-time throw is then contained to the page: the sidebar and
 * nav stay mounted, so every other surface remains reachable instead of the
 * app-root boundary replacing the viewport.
 */
const shellRoute = (Page: Component) => () => (
  <AppShell>
    <RouteErrorBoundary>
      <Page />
    </RouteErrorBoundary>
  </AppShell>
);

/**
 * Main Application Component
 *
 * Sets up routing with AppShell as the layout wrapper:
 * - `/login` → GitHub OAuth login page
 * - `/` → public landing page
 * - `/plan` → Plan surface
 * - `/board` → Kanban board view
 * - `/chat` → AI conversation interface
 * - `/items/:id` → Work item detail view
 */
const App: Component = () => {
  return (
    <TooltipProvider>
      <ToastProvider>
        <WebSocketProvider>
          <ErrorBoundary
            fallback={(err, reset) => {
              console.error("Unhandled render error:", err);
              const isRateLimited =
                err instanceof ApiError && err.status === 429;
              return (
                <div class="flex min-h-screen items-center justify-center bg-paper p-4">
                  <div class="w-full max-w-md text-center">
                    <div class="mb-4 text-signal-red">
                      <svg
                        class="mx-auto h-12 w-12"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                      </svg>
                    </div>
                    <h1 class="mb-2 text-lg font-semibold text-ink">
                      {isRateLimited
                        ? "Too many requests"
                        : "Something went wrong"}
                    </h1>
                    <p class="mb-6 text-sm text-ink-2">
                      {isRateLimited
                        ? "You're making requests faster than the server allows. Wait a few seconds, then try again."
                        : "An unexpected error occurred. You can try again or reload the page."}
                    </p>
                    <div class="flex items-center justify-center gap-3">
                      <button
                        class="inline-flex items-center justify-center rounded bg-ink px-4 py-2 text-sm font-medium text-paper transition-colors hover:bg-ink-2 focus-ring"
                        onClick={reset}
                      >
                        Try again
                      </button>
                      <button
                        class="inline-flex items-center justify-center rounded border border-line bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-sunken focus-ring"
                        onClick={() => window.location.reload()}
                      >
                        Reload page
                      </button>
                    </div>
                  </div>
                </div>
              );
            }}
          >
            <Router>
              {/* Login page - no AppShell */}
              <Route path="/login" component={LoginPage} />

              {/* Pending page - no AppShell (standalone like login) */}
              <Route path="/pending" component={PendingPage} />

              {/* Public invite acceptance page - no AppShell, no auth required */}
              <Route path="/invite/:token" component={InvitePage} />

              {/* GitHub App installation callback - no AppShell */}
              <Route
                path="/github/app/callback"
                component={GitHubAppCallbackPage}
              />

              {/* Public legal pages - no AppShell, no auth required */}
              <Route
                path="/terms"
                component={() => <LegalPage doc="terms" />}
              />
              <Route
                path="/privacy"
                component={() => <LegalPage doc="privacy" />}
              />

              {/* Public landing page - no AppShell, no auth required */}
              <Route path="/" component={LandingPage} />

              {/* Main routes wrapped in AppShell */}
              <Route path="/plan" component={shellRoute(PlanPage)} />
              <Route path="/board" component={shellRoute(BoardPage)} />
              <Route path="/backlog" component={shellRoute(BacklogPage)} />
              <Route path="/chat" component={shellRoute(ChatPage)} />
              <Route path="/documents" component={shellRoute(DocumentsPage)} />
              <Route
                path="/documents/:id"
                component={shellRoute(DocumentDetailPage)}
              />
              <Route path="/decisions" component={shellRoute(DecisionsPage)} />
              <Route path="/items/:id" component={shellRoute(ItemDetailPage)} />
              <Route path="/projects" component={shellRoute(ProjectsPage)} />
              <Route
                path="/projects/:id/settings"
                component={shellRoute(ProjectSettingsPage)}
              />
              <Route
                path="/settings/billing"
                component={shellRoute(BillingPage)}
              />

              {/* Fallback for unmatched routes */}
              <Route path="*" component={() => <Navigate href="/board" />} />
            </Router>
          </ErrorBoundary>
        </WebSocketProvider>
      </ToastProvider>
    </TooltipProvider>
  );
};

export default App;
